<template>
  <component :is="wrapperComponent" :key="componentIndex" class="base-form">
    <div class="base-form__content">
      <div
        v-for="(groupValues, groupName) in groups"
        :key="groupName"
        class="base-form__group"
        :style="groupStyles"
      >
        <div v-if="$slots[`group-label-${groupName}`]" class="base-form__group-label">
          <slot :name="`group-label-${groupName}`" />
        </div>
        <div
          v-for="(value, index) in groupValues"
          :key="value.name"
          class="base-form__field"
          :class="{
            ['base-form__field--error']: Boolean(
              validation.errors.value[value.metaName ?? value.name],
            ),
            ['base-form__field--disabled']: value.attrs?.disabled,
            ['base-form__field--placement-bottom']:
              value.labelIsVisible === false,
          }"
          :style="{
            ...getFieldStyle(value.layout),
            ...getHiddenStyle(value.hidden),
          }"
        >
          <div
            v-if="value.labelIsVisible !== false"
            class="base-form__field-label"
          >
            <slot
              :name="`label-${value.name}`"
              v-bind="{
                value: fieldComponent[value.type].props?.label ?? value.label,
                required: value.required,
              }"
            >
              <div>
                {{ fieldComponent[value.type].props?.label ?? value.label }}
                <span v-if="value.required">*</span>
              </div>
              <BaseIcon
                v-if="value.labelIcon"
                :name="value.labelIcon.name"
                size="18px"
                color="var(--color-foreground-caption)"
                clickable
                @click="value.labelIcon.onClick"
              />
            </slot>
          </div>
          <slot
            :name="`component-${value.metaName ?? value.name}`"
            v-bind="{
              component: fieldComponent[value.type].component,
              attrs: {
                'ref': (el: any) => componentRefs[value.name] = el,
                'modelValue': props.modelValue[value.name],
                'name': value.name,
                'error-message': value.required && validation.errors.value[value.name],
                'tabindex': (index + 1) * 10,
                'groupName': groupName,
                ...(fieldComponent[value.type].props ?? {}),
                ...(value.attrs ?? {}),
                'group': value.group,
                'label': value.label,
                'index': index,
              },
              handlers: {
                ...(fieldComponent[value.type].handlers?.(value.name) ?? {}),
                ...(value.handlers ?? {}),
              },
            }"
          >
            <component
              :is="fieldComponent[value.type].component"
              :ref="(el: any) => componentRefs[value.name] = el"
              :model-value="props.modelValue[value.name]"
              :name="value.name"
              :error-message="value.required && validation.errors.value[value.name]"
              v-bind="{
                tabindex: (index + 1) * 10,
                ...(fieldComponent[value.type].props ?? {}),
                ...(value.attrs ?? {}),
              }"
              v-on="{
                ...(fieldComponent[value.type].handlers?.(value.name) ?? {}),
                ...(value.handlers ?? {}),
              }"
            >
              <template v-if="['button', 'typography'].includes(value.type)" #default>
                {{ props.modelValue[value.name] }}
              </template>
            </component>
          </slot>
        </div>
        <slot name="append-group" v-bind="{ groupName }" />
      </div>
      <slot name="append-content" />
    </div>
    <div v-if="$slots.actions" class="base-form__actions">
      <slot
        name="actions"
        v-bind="{
          loading: loading.value,
          isValid: validation.isValid,
          errors: validation.errors,
          nextTabindex: (props.schema.length + 1) * 10,
          handleSubmitForm,
        }"
      />
    </div>
    <div v-if="props.withErrorMessage" class="base-form__errors">
      {{ errorMessage }}
    </div>
  </component>
</template>

<script
  lang="ts"
  setup
  generic="
    T extends Record<string, unknown>,
    K extends Extract<keyof T, string> = Extract<keyof T, string>
  "
>
import type { CSSProperties } from 'vue'
import type { SchemaType } from '../../lib/use/validation'
import isEqual from 'lodash/isEqual'
import { defineAsyncComponent } from 'vue'
import { REGEX_FOR_CHECK_PHONE } from '~/shared/const'
import { formatIsoCalendarDay } from '~/shared/lib/date'
import { FormField } from '../../const/formField'
import { useLoading } from '../../lib/use/loading'
import { useValidation } from '../../lib/use/validation'
import BaseIcon from '../icon/index.vue'

export interface Expose {
  componentRefs: Record<string, any>
  isValid: boolean
  errorMessage: string
  errors: string[]
  validate: () => Promise<{ valid: boolean }>
}

export interface Props<
  T extends Record<string, unknown>,
  K extends Extract<keyof T, string> = Extract<keyof T, string>,
> {
  modelValue: T
  schema: SchemaType<K>[]
  withFormWrapper?: boolean
  withErrorMessage?: boolean
  whenHandleSubmit?: (...args: any) => Promise<void>
  groupDirection?: 'row' | 'column'
  groupGap?: string
}

interface Emits {
  (e: 'update:modelValue', value: T): void
  (e: 'clear', value: K): void
}

const props = withDefaults(defineProps<Props<T, K>>(), {
  withFormWrapper: true,
  withErrorMessage: true,
  groupDirection: 'row',
})
const emit = defineEmits<Emits>()
const slots = defineSlots()
const loading = useLoading()
const hasGroups = computed(() =>
  props.schema.every(val => Boolean(val.group)),
)
const baseStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
}
const groupStyles = computed(() => ({
  '--group-flex-flow': props.groupDirection === 'column' ? 'column nowrap' : 'row wrap',
  '--group-gap': props.groupGap,
}))
const wrapperComponent = h(
  props.withFormWrapper ? 'form' : 'div',
  props.withFormWrapper
    ? {
        style: baseStyle,
        onSubmit: (e: Event) => {
          e.preventDefault()
          handleSubmitForm(props?.whenHandleSubmit)
        },
      }
    : { style: baseStyle },
  {
    default: () => slots.default?.(),
  },
)
const groups = computed(() => {
  if (!hasGroups.value) {
    return { none: props.schema }
  }

  return props.schema.reduce(
    (acc, val) => {
      if (!acc[val.group!]) {
        acc[val.group!] = []
      }

      acc[val.group!].push(val)

      return acc
    },
    {} as Record<string, Array<SchemaType<K>>>,
  )
})

const component = {
  typography: defineAsyncComponent(() => import('../typography/index.vue')),
  input: defineAsyncComponent(() => import('../input/index.vue')),
  phoneInput: defineAsyncComponent(() => import('../phone-input/index.vue')),
  phoneOrEmailInput: defineAsyncComponent(
    () => import('../phone-or-email-input/index.vue'),
  ),
  codeInput: defineAsyncComponent(() => import('../code-input/index.vue')),
  textarea: defineAsyncComponent(() => import('../textarea/index.vue')),
  password: defineAsyncComponent(() => import('../password-input/index.vue')),
  checkbox: defineAsyncComponent(() => import('../checkbox/index.vue')),
  checkboxGroup: defineAsyncComponent(() => import('../checkbox/group.vue')),
  radio: defineAsyncComponent(() => import('../radio/index.vue')),
  radioGroup: defineAsyncComponent(() => import('../radio/group.vue')),
  datePicker: defineAsyncComponent(() => import('../date-picker/index.vue')),
  timePicker: defineAsyncComponent(() => import('../time-picker/index.vue')),
  select: defineAsyncComponent(() => import('../select/index.vue')),
  selectTags: defineAsyncComponent(() => import('../select-tags/index.vue')),
  autocomplete: defineAsyncComponent(() => import('../autocomplete/index.vue')),
  button: defineAsyncComponent(() => import('../button/index.vue')),
  address: defineAsyncComponent(() => import('../address-input/index.vue')),
  inputMap: defineAsyncComponent(() => import('../map-input/index.vue')),
  fileUploader: defineAsyncComponent(() => import('../file-uploader/index.vue')),
  divider: defineAsyncComponent(() => import('../divider/index.vue')),
  colorPicker: defineAsyncComponent(() => import('../color-picker/index.vue')),
  iconPicker: defineAsyncComponent(() => import('../icon-picker/index.vue')),
}

interface FieldComponentValueType {
  component: unknown
  props?: Record<string, unknown>
  handlers?: (name: K) => Record<string, (...args: any[]) => void>
}

let validation!: ReturnType<typeof useValidation<T>>
const componentIndex = ref(0)
const componentRefs = ref<Record<string, any>>({})

function isEqualSchema(value: SchemaType<K>[], oldValue?: SchemaType<K>[]) {
  if (!oldValue) {
    return false
  }

  return value.length === oldValue.length && value.every((item, index) =>
    oldValue[index].group === item.group
    && oldValue[index].type === item.type
    && oldValue[index].name === item.name
    && oldValue[index].metaName === item.metaName
    && oldValue[index].label === item.label
    && oldValue[index].labelIsVisible === item.labelIsVisible
    && oldValue[index].labelIcon === item.labelIcon
    && oldValue[index].required === item.required,
  )
}

watch(
  () => props.schema,
  (value, oldValue) => {
    if (isEqualSchema(value, oldValue)) {
      return
    }

    validation = useValidation<T>(value, toRaw(props.modelValue))
  },
  { deep: true, immediate: true },
)
watch(
  () => props.modelValue,
  (newValue) => {
    const localModel = toRaw(validation.fields)
    if (!isEqual(newValue, localModel)) {
      updateValidationFields(validation.fields, newValue)
    }
  },
  { deep: true },
)

function updateValidationFields(target: typeof validation.fields, source: T) {
  Object.keys(source).forEach((key) => {
    if (!target[key]) {
      return
    }
    if (!isEqual(target[key].value, source[key])) {
      if (typeof source[key] === 'object' && source[key] !== null) {
        if (Array.isArray(source[key])) {
          target[key].value = [...source[key]] as T[K]
        }
      }
      else {
        target[key].value = source[key] as T[K]
      }
    }
  })
}

const fieldComponent: Record<FormField, FieldComponentValueType> = {
  [FormField.typography]: {
    component: component.typography,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.string]: {
    component: component.input,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.textarea]: {
    component: component.textarea,
    props: {},
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
      'clear': () => emit('clear', name),
    }),
  },
  [FormField.fio]: {
    component: component.input,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.nickname]: {
    component: component.input,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.number]: {
    component: component.input,
    props: {
      type: 'number',
      inputmode: 'numeric',
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.email]: {
    component: component.input,
    props: {
      withFull: true,
      placeholder: 'user@mail.ru',
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.password]: {
    component: component.password,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.repeatPassword]: {
    component: component.password,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.phone]: {
    component: component.phoneInput,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.phoneOrEmail]: {
    component: component.phoneOrEmailInput,
    props: { label: undefined, withFull: true },
    handlers(name: K) {
      return {
        'update:modelValue': (value: T[K]) => {
          const isPhone = REGEX_FOR_CHECK_PHONE.test(value as string)

          this.props!.label = !value
            ? undefined
            : isPhone || !Number.isNaN(+value)
              ? 'Телефон'
              : 'Почта'
          handleChangeValue(name, value)
        },
      }
    },
  },
  [FormField.checkbox]: {
    component: component.checkbox,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.checkboxGroup]: {
    component: component.checkboxGroup,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.radio]: {
    component: component.radio,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.radioGroup]: {
    component: component.radioGroup,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.code]: {
    component: component.codeInput,
    props: {
      length: 4,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.date]: {
    component: component.datePicker,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.time]: {
    component: component.timePicker,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.birthday]: {
    component: component.datePicker,
    props: {
      maxDate: new Date(),
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': (value: Date | string) => {
        handleChangeValue(
          name,
          (value instanceof Date ? formatIsoCalendarDay(value) : value) as T[K],
        )
      },
    }),
  },
  [FormField.select]: {
    component: component.select,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.selectTags]: {
    component: component.selectTags,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.autocomplete]: {
    component: component.autocomplete,
    props: {
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.button]: {
    component: component.button,
    props: {
      height: '38px',
      withFull: true,
    },
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.address]: {
    component: component.address,
    props: {},
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.inputMap]: {
    component: component.inputMap,
    props: {},
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.fileUploader]: {
    component: component.fileUploader,
    props: {},
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.divider]: {
    component: component.divider,
    props: {},
  },
  [FormField.colorPicker]: {
    component: component.colorPicker,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
  [FormField.iconPicker]: {
    component: component.iconPicker,
    handlers: (name: K) => ({
      'update:modelValue': handleChangeValue.bind(null, name),
    }),
  },
}

function getRawObject(obj: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(toRaw(obj)).map(([key, val]) => [key, toValue(val)]),
  )
}

const errorMessage = computed(() => {
  let message = ''

  Object.keys(validation.errors.value).forEach((fieldName) => {
    const label = props.schema.find(val => val.name === fieldName)?.label
    message += `${(label ? `${label}: ` : '') + validation.errors.value[fieldName]} | `
  })

  return message.slice(0, -3)
})
async function handleSubmitForm(
  callback?: (data: T) => Promise<void>,
): Promise<void | undefined> {
  await validation.validate()

  if (!validation.isValid.value) {
    return
  }

  const data = await validation.handleSubmit(() =>
    getRawObject(validation.fields),
  )()

  if (callback) {
    loading.wrapLoading(() => callback(data as T))
  }
}

function getHiddenStyle(values?: Array<'xs' | 'sm' | 'md' | 'lg' | 'xl'>) {
  return {
    '--hidden-xl': values?.includes('xl') ? 'none' : 'flex',
    '--hidden-lg': values?.includes('lg') ? 'none' : 'flex',
    '--hidden-md': values?.includes('md') ? 'none' : 'flex',
    '--hidden-sm': values?.includes('sm') ? 'none' : 'flex',
    '--hidden-xs': values?.includes('xs') ? 'none' : 'flex',
  }
}

function getFieldStyle(layout?: {
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
}) {
  if (!layout) {
    return {}
  }
  return {
    '--field-xl': layout.xl,
    '--field-lg': layout.lg,
    '--field-md': layout.md,
    '--field-sm': layout.sm,
    '--field-xs': layout.xs,
  }
}

function handleChangeValue(fieldName: K, value: T[K]) {
  validation.fields[fieldName].value = value
  emit('update:modelValue', {
    ...props.modelValue,
    [fieldName]: value,
  })
}

async function validate() {
  componentIndex.value++
  return validation.validate()
}

defineExpose({
  isDirty: validation.isDirty,
  isValid: validation.isValid,
  loading: loading.value,
  errorMessage,
  errors: validation.errors,
  validate,
  componentRefs,
})
</script>

<style lang="scss" scoped>
.base-form {
  position: relative;
  display: flex;
  flex-direction: column;

  &__content {
    display: flex;
    flex-direction: column;
    gap: rem(24px);
  }

  &__group {
    position: relative;
    display: flex;
    flex-flow: var(--group-flex-flow, row wrap);
    gap: var(--group-gap, rem(14px));

    &:not(:last-child)::after {
      content: "";
      display: block;
      width: 100%;
      height: 2px;
      background-color: var(--color-surface-placeholder);
      margin-top: rem(10px);
    }

    &-label {
      flex: 1 1 100%;
    }
  }

  &__field {
    flex: 1 1 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    @include bp-media('xl') {
      flex-basis: calc(100% * var(--field-xl, 1) - 4%);
      display: var(--hidden-xl);
    }

    @include bp-media('lg') {
      flex-basis: calc(100% * var(--field-lg, 1) - 4%);
      display: var(--hidden-lg);
    }

    @include bp-media('md') {
      flex-basis: calc(100% * var(--field-md, 1) - 4%);
      display: var(--hidden-md);
    }

    @include bp-media('sm') {
      flex-basis: calc(100% * var(--field-sm, 1) - 4%);
      display: var(--hidden-sm);
    }

    @include bp-media('xs') {
      flex-basis: calc(100% * var(--field-xs, 1) - 4%);
      display: var(--hidden-xs);
    }

    &-label {
      @include typo-label;

      font-weight: 700;
      margin-bottom: rem(8px);
      display: flex;
      justify-content: space-between;

      &:deep(> div > span) {
        color: var(--color-foreground-danger);
      }

      > div {
        white-space: nowrap;
      }
    }

    &--error {
      .base-form__field-label {
        color: var(--color-foreground-danger);
      }
    }

    &--disabled {
      .base-form__field-label {
        color: var(--color-foreground-caption);
      }
    }

    &--placement-bottom {
      justify-content: flex-end;
    }
  }

  &__errors {
    @include typo-caption;

    position: absolute;
    bottom: rem(-28px);
    font-weight: 600;
    color: var(--color-foreground-danger);
    max-height: 1.4rem;
    height: 100%;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    z-index: 1;
  }

  &__actions {
    flex: 1 1 100%;
    display: flex;
    margin-top: rem(24px);
    gap: rem(12px);
  }
}
</style>
